import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";

import {
  AccountBillingDomainError,
  createBrowserAccountBillingApi,
  type BillingAddress,
  type BillingProfile,
} from "./account-billing-api";
import { AccountChrome } from "./site-chrome";

export const ACCOUNT_BILLING_PATH = "/account/billing";

function addressLabel(address: BillingAddress): string {
  return [address.company, address.streetLine1, address.city, address.postalCode].filter(Boolean).join(", ");
}

export function AccountBillingLauncher(): ReactElement | null {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path !== "/account" && path !== "/account/settings") return null;
  return <a className="account-billing-launcher" href={ACCOUNT_BILLING_PATH}>פרטי חיוב</a>;
}

export function AccountBillingPage(): ReactElement {
  const api = useMemo(() => createBrowserAccountBillingApi(), []);
  const [profile, setProfile] = useState<BillingProfile | null | undefined>(undefined);
  const [billingName, setBillingName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [busy, setBusy] = useState(false);
  const [addressBusy, setAddressBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const applyProfile = (next: BillingProfile | null) => {
    setProfile(next);
    if (!next) return;
    setBillingName(next.billingName ?? next.businessName ?? `${next.firstName} ${next.lastName}`.trim());
    setBillingEmail(next.billingEmail ?? next.emailAddress);
    const preferred = next.addresses.find(address => address.defaultBillingAddress) ?? next.addresses[0] ?? null;
    setSelectedAddressId(preferred?.id ?? "");
  };

  useEffect(() => {
    const controller = new AbortController();
    void api.load(controller.signal).then(applyProfile).catch(() => {
      setError("לא הצלחנו לטעון את פרטי החיוב כרגע.");
      setProfile(null);
    });
    return () => controller.abort();
  }, [api]);

  const saveBilling = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile || busy) return;
    if (!billingName.trim() || !billingEmail.trim()) {
      setError("יש למלא שם לחיוב ואימייל לקבלת מסמכים.");
      return;
    }
    if (profile.customerType === "business" && (!profile.businessName || !profile.businessId)) {
      setError("לפני שמירת פרטי חיוב עסקיים יש להשלים שם עסק ומספר עוסק / ח.פ. בפרטי החשבון.");
      return;
    }
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const next = await api.save(profile, billingName, billingEmail);
      applyProfile(next);
      setMessage("פרטי החיוב נשמרו.");
    } catch (cause) {
      setError(cause instanceof AccountBillingDomainError ? cause.message : "לא הצלחנו לשמור את פרטי החיוב.");
    } finally {
      setBusy(false);
    }
  };

  const saveDefaultAddress = async () => {
    if (!profile || !selectedAddressId || addressBusy) return;
    const address = profile.addresses.find(item => item.id === selectedAddressId);
    if (!address) return;
    setAddressBusy(true);
    setMessage(null);
    setError(null);
    try {
      const next = await api.setDefaultBillingAddress(address);
      applyProfile(next);
      setMessage("כתובת ברירת המחדל לחיוב עודכנה.");
    } catch {
      setError("לא הצלחנו לעדכן את כתובת החיוב.");
    } finally {
      setAddressBusy(false);
    }
  };

  if (profile === undefined && !error) {
    return <AccountChrome><main className="account-status-page">טוענים פרטי חיוב…</main></AccountChrome>;
  }

  if (!profile) {
    return (
      <AccountChrome>
        <main className="account-status-page">
          <section>
            <h1>צריך להתחבר לחשבון</h1>
            <p>{error ?? "פרטי החיוב זמינים רק לבעל החשבון."}</p>
            <a className="account-primary-button account-button-link" href="/login">לכניסה</a>
          </section>
        </main>
      </AccountChrome>
    );
  }

  const isBusiness = profile.customerType === "business";

  return (
    <AccountChrome>
      <main className="account-page account-billing-page">
        <div className="account-billing-heading">
          <div>
            <span className="account-kicker">האזור האישי</span>
            <h1>פרטי חיוב</h1>
            <p>הפרטים שיישמרו עבור מסמכי חיוב עתידיים.</p>
          </div>
          <a className="account-secondary-button" href="/account">חזרה לאזור האישי</a>
        </div>

        {message ? <div className="account-success" role="status">{message}</div> : null}
        {error ? <div className="account-error" role="alert">{error}</div> : null}

        <section className="account-panel account-billing-section">
          <div className="account-panel-heading">
            <div>
              <span className="account-kicker">פרטי מסמך</span>
              <h2>{isBusiness ? "חיוב לעסק" : "חיוב ללקוח פרטי"}</h2>
            </div>
            <span className="account-billing-badge">{isBusiness ? "חשבון עסקי" : "חשבון פרטי"}</span>
          </div>

          {isBusiness ? (
            <div className="account-billing-business-summary">
              <div><span>שם העסק</span><strong>{profile.businessName || "לא הוגדר"}</strong></div>
              <div><span>עוסק / ח.פ.</span><strong>{profile.businessId || "לא הוגדר"}</strong></div>
              <a href="/account/settings">עריכת פרטי העסק</a>
            </div>
          ) : null}

          <form className="account-form" onSubmit={event => void saveBilling(event)}>
            <div className="account-form-grid">
              <label>
                <span>שם לחיוב</span>
                <input required autoComplete="organization" value={billingName} onChange={event => setBillingName(event.target.value)} />
                <small>השם שיופיע על מסמך החיוב.</small>
              </label>
              <label>
                <span>אימייל למסמכים</span>
                <input required type="email" autoComplete="email" value={billingEmail} onChange={event => setBillingEmail(event.target.value)} />
                <small>יכול להיות שונה מאימייל הכניסה לחשבון.</small>
              </label>
            </div>
            <div><button className="account-primary-button" type="submit" disabled={busy}>{busy ? "שומר…" : "שמירת פרטי חיוב"}</button></div>
          </form>
        </section>

        <section className="account-panel account-billing-section">
          <div className="account-panel-heading">
            <div><span className="account-kicker">כתובת חיוב</span><h2>ברירת מחדל לחשבונית</h2></div>
            <a className="account-secondary-button" href="/account/settings">ניהול כתובות</a>
          </div>

          {profile.addresses.length === 0 ? (
            <div className="account-empty-list">
              <p>אין עדיין כתובת שמורה. הוסיפו כתובת בפרטי החשבון וסמנו אותה כברירת מחדל לחיוב.</p>
              <a href="/account/settings">להוספת כתובת</a>
            </div>
          ) : (
            <div className="account-billing-address-row">
              <label>
                <span>כתובת לחיוב</span>
                <select value={selectedAddressId} onChange={event => setSelectedAddressId(event.target.value)}>
                  {profile.addresses.map(address => (
                    <option key={address.id} value={address.id}>
                      {address.defaultBillingAddress ? "ברירת מחדל — " : ""}{addressLabel(address)}
                    </option>
                  ))}
                </select>
              </label>
              <button className="account-primary-button" type="button" disabled={!selectedAddressId || addressBusy} onClick={() => void saveDefaultAddress()}>
                {addressBusy ? "מעדכן…" : "הגדרה כברירת מחדל לחיוב"}
              </button>
            </div>
          )}
        </section>

        <section className="account-panel account-billing-note">
          <strong>מסמכים חשבונאיים</strong>
          <p>המערכת שומרת את פרטי החיוב בלבד. הורדת חשבונית או קבלה תופעל רק לאחר חיבור מקור חשבונאי אמיתי.</p>
        </section>
      </main>
    </AccountChrome>
  );
}
